/**
 * RevenueAssetsPanel — full CRUD UI for Revenue Assets (offers).
 *
 * Backed by /api/assets. Replaces the legacy Products screen with a unified
 * editor that supports every asset type (digital_product, course, webinar,
 * membership, booking, coaching, community, downloadable) plus the full
 * pricing + currency + delivery configuration the operational loop needs.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Plus, Loader2, Save, Trash2, ShoppingBag, Search, Copy, X, Globe2, EyeOff, Eye,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const ASSET_TYPES = [
  { value: "digital_product", label: "Digital product" },
  { value: "course", label: "Course" },
  { value: "webinar", label: "Webinar" },
  { value: "membership", label: "Membership" },
  { value: "booking", label: "Booking" },
  { value: "coaching", label: "Coaching" },
  { value: "community", label: "Community" },
  { value: "downloadable", label: "Downloadable" },
];

const DELIVERY_KINDS = ["url", "pdf", "drive", "youtube", "vimeo", "files", "webinar", "membership"];

const blank = (currencies) => ({
  type: "digital_product",
  title: "",
  description: "",
  pricing: { mode: "one_time", currency: currencies[0] || "USD", price: 49.0 },
  delivery: { kind: "url", redirect_url: "", instructions: "" },
  public_visible: true,
});

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-meta font-bold block mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full rounded-lg border border-line bg-white px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500";

export default function RevenueAssetsPanel() {
  const [assets, setAssets] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [currencies, setCurrencies] = useState(["USD"]);
  const [supportedCurrencies, setSupportedCurrencies] = useState(["USD"]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, ps] = await Promise.all([
        api.get("/assets"),
        api.get("/payments/settings").catch(() => ({ data: null })),
      ]);
      setAssets(a.data || []);
      const list = ps.data?.supported_currencies || ["USD"];
      setSupportedCurrencies(list);
      setCurrencies(list);
    } catch {
      setAssets([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fetch active asset details
  useEffect(() => {
    if (!activeId) { setActive(null); return; }
    api.get(`/assets/${activeId}`).then((r) => setActive(r.data)).catch(() => setActive(null));
  }, [activeId]);

  const filtered = useMemo(() => {
    if (!assets) return [];
    const q = search.trim().toLowerCase();
    return q ? assets.filter((a) => (a.title || "").toLowerCase().includes(q)) : assets;
  }, [assets, search]);

  const dirty = useMemo(() => {
    if (!active || !assets) return false;
    const orig = assets.find((a) => a.id === active.id);
    if (!orig) return false;
    return JSON.stringify({ title: orig.title, description: orig.description, pricing: orig.pricing, delivery: orig.delivery, public_visible: orig.public_visible })
      !== JSON.stringify({ title: active.title, description: active.description, pricing: active.pricing, delivery: active.delivery, public_visible: active.public_visible });
  }, [active, assets]);

  const createAsset = async (payload) => {
    setBusy(true);
    try {
      const r = await api.post("/assets", payload);
      toast.success("Asset created");
      await load();
      setActiveId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create");
    }
    setBusy(false);
    setCreating(false);
  };

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await api.patch(`/assets/${active.id}`, {
        title: active.title,
        description: active.description,
        pricing: active.pricing,
        delivery: active.delivery,
        public_visible: active.public_visible,
      });
      setActive(r.data);
      await load();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const del = async () => {
    if (!active || !window.confirm(`Delete "${active.title}"?`)) return;
    try {
      await api.delete(`/assets/${active.id}`);
      setActiveId(null);
      setActive(null);
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const duplicate = async () => {
    if (!active) return;
    const cp = { ...active, title: `${active.title} (copy)` };
    delete cp.id; delete cp.created_at; delete cp.updated_at; delete cp.stats; delete cp.slug;
    await createAsset(cp);
  };

  if (assets === null) {
    return <div className="p-10 inline-flex items-center gap-2 text-meta"><Loader2 className="h-4 w-4 animate-spin" /> Loading assets…</div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-4 sm:p-6 lg:p-8" data-testid="revenue-assets-panel">
      <div className="grid lg:grid-cols-[320px_minmax(0,1fr)] gap-4">

        {/* Left rail */}
        <aside className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-bold inline-flex items-center gap-2">
              <ShoppingBag className="h-4 w-4 text-ai" /> Revenue assets
            </h2>
            <button
              onClick={() => setCreating(true)}
              data-testid="asset-new-btn"
              className="rounded-full ashra-gradient-bg px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 shadow-sm hover:shadow-md"
            >
              <Plus className="h-3 w-3" /> New
            </button>
          </div>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2 text-meta" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…" data-testid="asset-search"
              className="w-full rounded-xl border border-line bg-white pl-7 pr-3 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-white p-5 text-center text-xs text-meta" data-testid="assets-empty">
              {assets.length === 0 ? "No assets yet — create your first offer." : "No matches."}
            </div>
          ) : (
            <div className="space-y-1.5">
              {filtered.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setActiveId(a.id)}
                  data-testid={`asset-row-${a.id}`}
                  className={`w-full text-left rounded-xl border ${activeId === a.id ? "border-ai bg-ai-soft" : "border-line bg-white"} px-3 py-2 hover:border-ai transition`}
                >
                  <p className="text-sm font-semibold text-ink truncate">{a.title}</p>
                  <p className="text-[11px] text-meta truncate">
                    {(a.type || "").replace("_", " ")} · {(a.pricing?.currency || "USD")} {(a.pricing?.price || 0).toFixed(2)}
                    {a.public_visible === false ? " · hidden" : ""}
                  </p>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Right pane */}
        <main data-testid="asset-editor">
          {!active ? (
            <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
              <ShoppingBag className="h-8 w-8 text-zinc-300 mx-auto" />
              <p className="mt-2 text-sm font-medium text-zinc-700">Select an asset on the left to edit, or create a new one.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header */}
              <div className="surface p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <input
                      value={active.title || ""}
                      onChange={(e) => setActive({ ...active, title: e.target.value })}
                      data-testid="asset-title-input"
                      className="w-full font-display text-xl font-bold text-ink bg-transparent border-0 outline-none focus:ring-0"
                    />
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <span className="pill pill-neutral">{(active.type || "").replace("_", " ")}</span>
                      <span className="text-meta">{active.slug && `/${active.slug}`}</span>
                      <button
                        onClick={() => setActive({ ...active, public_visible: !active.public_visible })}
                        className={`pill ${active.public_visible ? "pill-success" : "pill-warning"} cursor-pointer`}
                        data-testid="asset-visibility-toggle"
                      >
                        {active.public_visible ? <><Eye className="h-3 w-3" /> Public</> : <><EyeOff className="h-3 w-3" /> Hidden</>}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={duplicate} className="rounded-full bg-zinc-100 px-3 py-1.5 text-xs font-semibold text-ink-soft hover:bg-zinc-200 inline-flex items-center gap-1" data-testid="asset-duplicate-btn">
                      <Copy className="h-3 w-3" /> Duplicate
                    </button>
                    <button onClick={save} disabled={busy || !dirty} data-testid="asset-save-btn" className="rounded-full ashra-gradient-bg px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50">
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
                    </button>
                    <button onClick={del} className="rounded-full bg-zinc-100 hover:bg-rose-100 hover:text-danger text-meta p-1.5" data-testid="asset-delete-btn">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <Field label="Description">
                    <textarea
                      value={active.description || ""} rows={3}
                      onChange={(e) => setActive({ ...active, description: e.target.value })}
                      placeholder="What does the buyer get?"
                      data-testid="asset-description-input"
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                </div>
              </div>

              {/* Pricing + currency */}
              <div className="surface p-5" data-testid="asset-pricing-card">
                <h3 className="font-display text-base font-bold text-ink mb-3">Pricing</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Field label="Mode">
                    <select
                      value={active.pricing?.mode || "one_time"}
                      onChange={(e) => setActive({ ...active, pricing: { ...active.pricing, mode: e.target.value } })}
                      data-testid="asset-pricing-mode"
                      className={inputCls}
                    >
                      <option value="one_time">One-time</option>
                      <option value="subscription">Subscription</option>
                      <option value="free">Free</option>
                    </select>
                  </Field>
                  <Field label="Currency">
                    <select
                      value={(active.pricing?.currency || "USD").toUpperCase()}
                      onChange={(e) => setActive({ ...active, pricing: { ...active.pricing, currency: e.target.value } })}
                      data-testid="asset-pricing-currency"
                      className={inputCls}
                    >
                      {supportedCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Price">
                    <input
                      type="number" step="0.01" min="0"
                      value={active.pricing?.price ?? 0}
                      onChange={(e) => setActive({ ...active, pricing: { ...active.pricing, price: parseFloat(e.target.value) || 0 } })}
                      data-testid="asset-pricing-price"
                      className={inputCls}
                    />
                  </Field>
                  {active.pricing?.mode === "subscription" && (
                    <Field label="Interval">
                      <select
                        value={active.pricing?.interval || "month"}
                        onChange={(e) => setActive({ ...active, pricing: { ...active.pricing, interval: e.target.value } })}
                        data-testid="asset-pricing-interval"
                        className={inputCls}
                      >
                        <option value="month">Monthly</option>
                        <option value="year">Yearly</option>
                      </select>
                    </Field>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-meta inline-flex items-center gap-1">
                  <Globe2 className="h-3 w-3" /> Currency overrides the workspace default. Pick from your Settings → Payments supported list.
                </p>
              </div>

              {/* Delivery */}
              <div className="surface p-5" data-testid="asset-delivery-card">
                <h3 className="font-display text-base font-bold text-ink mb-3">Delivery</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Method">
                    <select
                      value={active.delivery?.kind || "url"}
                      onChange={(e) => setActive({ ...active, delivery: { ...active.delivery, kind: e.target.value } })}
                      data-testid="asset-delivery-kind"
                      className={inputCls}
                    >
                      {DELIVERY_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </Field>
                  <Field label={active.delivery?.kind === "pdf" ? "PDF URL" : active.delivery?.kind === "drive" ? "Drive URL" : active.delivery?.kind === "youtube" || active.delivery?.kind === "vimeo" ? "Video URL" : "Access URL"}>
                    <input
                      value={active.delivery?.redirect_url || active.delivery?.pdf_url || active.delivery?.drive_url || active.delivery?.video_url || ""}
                      onChange={(e) => {
                        const k = active.delivery?.kind || "url";
                        const map = { pdf: "pdf_url", drive: "drive_url", youtube: "video_url", vimeo: "video_url" };
                        const field = map[k] || "redirect_url";
                        setActive({ ...active, delivery: { ...active.delivery, [field]: e.target.value } });
                      }}
                      placeholder="https://…"
                      data-testid="asset-delivery-url"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="mt-3">
                  <Field label="Instructions (shown on portal access)">
                    <textarea
                      value={active.delivery?.instructions || ""} rows={2}
                      onChange={(e) => setActive({ ...active, delivery: { ...active.delivery, instructions: e.target.value } })}
                      placeholder="Welcome! Click below to access your purchase."
                      data-testid="asset-delivery-instructions"
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {creating && (
        <CreateModal
          currencies={supportedCurrencies}
          onCancel={() => setCreating(false)}
          onSubmit={createAsset}
          busy={busy}
        />
      )}
    </div>
  );
}

function CreateModal({ currencies, onCancel, onSubmit, busy }) {
  const [form, setForm] = useState(() => blank(currencies));
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm grid place-items-center z-50" onClick={onCancel} data-testid="asset-create-modal">
      <div className="bg-white rounded-3xl border border-line shadow-xl p-6 w-[min(560px,92vw)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold inline-flex items-center gap-2"><ShoppingBag className="h-4 w-4 text-ai" /> Create offer</h3>
          <button onClick={onCancel} disabled={busy} className="text-meta hover:text-ink"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Title">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Premium playbook" data-testid="asset-create-title" className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="asset-create-type" className={inputCls}>
                {ASSET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Currency">
              <select value={form.pricing.currency} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, currency: e.target.value } })} data-testid="asset-create-currency" className={inputCls}>
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price">
              <input type="number" step="0.01" min="0" value={form.pricing.price} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, price: parseFloat(e.target.value) || 0 } })} data-testid="asset-create-price" className={inputCls} />
            </Field>
            <Field label="Delivery">
              <select value={form.delivery.kind} onChange={(e) => setForm({ ...form, delivery: { ...form.delivery, kind: e.target.value } })} className={inputCls}>
                {DELIVERY_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Access URL (where buyers go after payment)">
            <input value={form.delivery.redirect_url} onChange={(e) => setForm({ ...form, delivery: { ...form.delivery, redirect_url: e.target.value } })} placeholder="https://…" data-testid="asset-create-url" className={inputCls} />
          </Field>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="rounded-full bg-zinc-100 px-3 py-1.5 text-xs font-semibold text-ink-soft">Cancel</button>
          <button
            onClick={() => {
              if (!form.title) { toast.error("Title required"); return; }
              onSubmit(form);
            }}
            disabled={busy}
            data-testid="asset-create-submit"
            className="rounded-full ashra-gradient-bg px-4 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Create
          </button>
        </div>
      </div>
    </div>
  );
}
